import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { TourProvider } from "@/context/TourContext";

import RequireAuth from "@/components/auth/RequireAuth";
import AppLayout from "@/components/layout/AppLayout";
import {
  DashboardPage, WorkOrdersPage, 
  AssetsPage, AssetDetailPage, ContainersPage, ContainerDetailPage,
  TriagePage, WipePage, RepairPage, QAPage,
  ExceptionsPage, InventoryPage, ShipmentsPage, ContactsPage as LegacyContactsPage,
  CompliancePage, DocumentsPage, ReportsPage,
} from "@/pages/ModulePages";
// SettingsPage replaced by AdminSettingsPage
import ProcessingQueuePage from "@/pages/ProcessingQueuePage";
import WorkOrderCreatePage from "@/pages/WorkOrderCreatePage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import IntakePage from "@/pages/IntakePage";
import IntakeSummaryPage from "@/pages/IntakeSummaryPage";
import WorkOrderPickupPage from "@/pages/WorkOrderPickupPage";
import AssetResolverPage from "./pages/AssetResolverPage";
import LoginPage from "./pages/LoginPage";
import NotFound from "./pages/NotFound";

// CRM Pages
import CrmDashboardPage from "@/pages/crm/CrmDashboardPage";
import CrmContactsPage from "@/pages/crm/ContactsPage";
import ContactDetailPage from "@/pages/crm/ContactDetailPage";
import DealsPage from "@/pages/crm/DealsPage";
import AppointmentsPage from "@/pages/crm/AppointmentsPage";
import CrmCustomersPage from "@/pages/crm/CustomersPage";
import CustomerDetailPage from "@/pages/crm/CustomerDetailPage";
// Admin Pages
import UsersPage from "@/pages/admin/UsersPage";
import TenantsPage from "@/pages/admin/TenantsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import EmailConfigPage from "@/pages/admin/EmailConfigPage";
import VapiConfigPage from "@/pages/admin/VapiConfigPage";
import IntegrationsPage from "@/pages/admin/IntegrationsPage";

// Profile Pages
import ProfilePage from "@/pages/ProfilePage";
import ProfileSettingsPage from "@/pages/ProfileSettingsPage";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppProvider>
        <TourProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <AppLayout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      
                      {/* Work Orders */}
                      <Route path="/work-orders" element={<WorkOrdersPage />} />
                      <Route path="/work-orders/new" element={<WorkOrderCreatePage />} />
                      <Route path="/work-orders/:id" element={<WorkOrderDetailPage />} />
                      <Route path="/work-orders/:id/intake" element={<IntakePage />} />
                      <Route path="/work-orders/:id/intake-summary" element={<IntakeSummaryPage />} />
                      <Route path="/work-orders/:id/pickup" element={<WorkOrderPickupPage />} />
                      
                      {/* Legacy redirects */}
                      <Route path="/jobs" element={<Navigate to="/work-orders" replace />} />
                      <Route path="/jobs/:id" element={<Navigate to="/work-orders" replace />} />
                      
                      {/* Containers */}
                      <Route path="/containers" element={<ContainersPage />} />
                      <Route path="/containers/:id" element={<ContainerDetailPage />} />
                      
                      {/* Assets */}
                      <Route path="/assets" element={<AssetsPage />} />
                      <Route path="/assets/:id" element={<AssetDetailPage />} />
                      
                      {/* Legacy redirects */}
                      <Route path="/inventory" element={<InventoryPage />} />
                      <Route path="/inventory/:id" element={<Navigate to="/assets" replace />} />
                      
                      {/* Processing */}
                      <Route path="/processing" element={<ProcessingQueuePage />} />
                      <Route path="/processing/triage" element={<TriagePage />} />
                      <Route path="/processing/wipe" element={<WipePage />} />
                      <Route path="/processing/repair" element={<RepairPage />} />
                      <Route path="/processing/qa" element={<QAPage />} />
                      
                      {/* Other modules */}
                      <Route path="/exceptions" element={<ExceptionsPage />} />
                      <Route path="/shipments" element={<ShipmentsPage />} />
                      <Route path="/contacts" element={<LegacyContactsPage />} />
                      <Route path="/compliance" element={<CompliancePage />} />
                      <Route path="/documents" element={<DocumentsPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
                      
                      {/* CRM */}
                      <Route path="/crm" element={<CrmDashboardPage />} />
                      <Route path="/crm/contacts" element={<CrmContactsPage />} />
                      <Route path="/crm/contacts/:id" element={<ContactDetailPage />} />
                      <Route path="/crm/deals" element={<DealsPage />} />
                      <Route path="/crm/appointments" element={<AppointmentsPage />} />
                      <Route path="/crm/customers" element={<CrmCustomersPage />} />
                      <Route path="/crm/customers/:id" element={<CustomerDetailPage />} />
                      
                      {/* Admin */}
                      <Route path="/admin/users" element={<UsersPage />} />
                      <Route path="/admin/tenants" element={<TenantsPage />} />
                      <Route path="/admin/settings" element={<AdminSettingsPage />} />
                      <Route path="/admin/email" element={<EmailConfigPage />} />
                      <Route path="/admin/vapi" element={<VapiConfigPage />} />
                      <Route path="/admin/integrations" element={<IntegrationsPage />} />
                      
                      {/* Profile */}
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/profile/settings" element={<ProfileSettingsPage />} />
                      
                      {/* QR Resolver Route */}
                      <Route path="/i/:assetUid" element={<AssetResolverPage />} />
                      
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </AppLayout>
                </RequireAuth>
              }
            />
          </Routes>
        </BrowserRouter>
        </TourProvider>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
